<form [formGroup]="eventForm">
  @if (!isEdit) {
    <section class="event-name-type">
      <fieldset class="inputAndErrorFieldSet">
        <mat-form-field
          appearance="outline"
          subscriptSizing="dynamic"
          class="event-name"
        >
          <mat-label>{{ eventTitle.EVENT_NAME }}</mat-label>
          <input
            matInput
            formControlName="eventName"
            type="text"
            placeholder="Add event name"
          />
        </mat-form-field>
        @if (
          eventForm.get("eventName")?.hasError("required") &&
          eventForm.get("eventName")?.touched
        ) {
          <mat-error class="matError">
            {{ eventMessages.EVENT_NAME_REQUIRED }}
          </mat-error>
        }
      </fieldset>
      <fieldset class="inputAndErrorFieldSet">
        <mat-form-field
          appearance="outline"
          subscriptSizing="dynamic"
          class="event-type"
        >
          <mat-label class="mat-label">{{ eventTitle.EVENT_TYPE }}</mat-label>
          <mat-select placeholder="Add event type" formControlName="eventType">
            @for (type of eventTypes; track type; let i = $index) {
              <mat-option [value]="type">
                {{ type }}
              </mat-option>
            }
          </mat-select>
        </mat-form-field>
        @if (
          eventForm.get("eventType")?.hasError("required") &&
          eventForm.get("eventType")?.touched
        ) {
          <mat-error class="matError">
            {{ eventMessages.EVENT_TYPE_REQUIRED }}
          </mat-error>
        }
      </fieldset>
    </section>
  }

  @if (!isEdit) {
    <section class="event-description-image">
      <fieldset class="inputAndErrorFieldSet">
        <section class="description-image-container">
          <mat-form-field
            appearance="outline"
            subscriptSizing="dynamic"
            class="event-description"
          >
            <mat-label class="mat-label">{{
              eventTitle.EVENT_DESCRIPTION
            }}</mat-label>
            <textarea
              matInput
              placeholder="Add event description"
              formControlName="description"
            ></textarea>
          </mat-form-field>
          <app-upload-files
            [fieldLabel]="'Event Image'"
            class="event-image"
            (fileEmitter)="onEventImageUploaded($event)"
            [eventImage]="eventImage"
            [back]="back"
          ></app-upload-files>
        </section>
        @if (
          eventForm.get("description")?.hasError("required") &&
          eventForm.get("description")?.touched
        ) {
          <mat-error class="matError">
            {{ eventMessages.EVENT_DESCRIPTION_REQUIRED }}
          </mat-error>
        }
      </fieldset>
    </section>
  }

  <section class="event-date-time-container">
    <h3>Event date and time</h3>
    <section class="event-date">
      <mat-form-field appearance="outline" subscriptSizing="dynamic">
        <mat-label class="mat-label">{{ eventTitle.START_DATE }}</mat-label>
        <input
          matInput
          [matDatepicker]="startPicker"
          formControlName="startDate"
          placeholder="Add start date"
        />
        <mat-icon matPrefix class="material-symbols-outlined"
          >calendar_today</mat-icon
        >
        @if (
          eventForm.get("startDate")?.hasError("required") &&
          eventForm.get("startDate")?.touched
        ) {
          <mat-error class="matError">
            {{ eventMessages.START_DATE_REQUIRED }}
          </mat-error>
        }
        @if (eventForm.get("startDate")?.hasError("notInPast")) {
          <mat-error class="matError">
            {{ eventMessages.PAST_START_DATE }}
          </mat-error>
        }
        <mat-datepicker-toggle matSuffix [for]="startPicker">
          <mat-icon matDatepickerToggleIcon>arrow_drop_down</mat-icon>
        </mat-datepicker-toggle>
        <mat-datepicker #startPicker></mat-datepicker>
      </mat-form-field>
      <app-time-picker [timeControl]="startTimeControl"></app-time-picker>

      <span>&#9472;&#9472;</span>

      <mat-form-field appearance="outline" subscriptSizing="dynamic">
        <mat-label class="mat-label">{{ eventTitle.END_DATE }}</mat-label>
        <input
          matInput
          [matDatepicker]="endPicker"
          formControlName="endDate"
          placeholder="Add end date"
        />
        <mat-icon matPrefix class="material-symbols-outlined"
          >calendar_today</mat-icon
        >
        @if (
          eventForm.get("endDate")?.hasError("required") &&
          eventForm.get("endDate")?.touched
        ) {
          <mat-error class="matError">
            {{ eventMessages.END_DATE_REQUIRED }}
          </mat-error>
        }
        @if (eventForm.get("endDate")?.hasError("endAfterStart")) {
          <mat-error class="matError">
            {{ eventMessages.END_BEFORE_START_DATE }}
          </mat-error>
        }
        <mat-datepicker-toggle matSuffix [for]="endPicker">
          <mat-icon matDatepickerToggleIcon>arrow_drop_down</mat-icon>
        </mat-datepicker-toggle>
        <mat-datepicker #endPicker></mat-datepicker>
      </mat-form-field>
      <app-time-picker [timeControl]="endTimeControl"></app-time-picker>
    </section>
  </section>

  <section class="event-address-container">
    <section class="location-title-radio">
      <h3>Event location</h3>
      <section class="event-locations-radio">
        <mat-radio-group formControlName="eventLocationType">
          <mat-radio-button
            value="{{ locationTypes.PHYSICAL }}"
            color="primary"
            >{{ eventTitle.PHYSICAL_LOCATION }}</mat-radio-button
          >
          <mat-radio-button
            value="{{ locationTypes.ONLINE }}"
            color="primary"
            >{{ eventTitle.ONLINE_MEETING }}</mat-radio-button
          >
          <mat-radio-button value="{{ locationTypes.BOTH }}" color="primary">{{
            eventTitle.PHYSICAL_ONLINE
          }}</mat-radio-button>
        </mat-radio-group>
      </section>
    </section>
    <section
      class="physical-location-container"
      [hidden]="eventLocationType === locationTypes.ONLINE"
    >
      <p>Physical location/s</p>
      <ng-container formArrayName="physicalLocations">
        @for (group of filteredPhysicalLocations; track group; let i = $index) {
          <app-address-form
            [locations]="locations"
            [addressForm]="getFormGroup(group)"
            [formNumber]="i + 1"
            [canBeDeleted]="canBeDeleted()"
            [usedLocationNames]="getUsedLocationNames()"
            (formDeleted)="removeLocation(i)"
          ></app-address-form>
        }
      </ng-container>
      <button mat-flat-button color="accent" (click)="addNewLocation()">
        {{ buttonAction.ADD_NEW_LOCATION }}
      </button>
    </section>
    @if (
      eventLocationType === locationTypes.ONLINE ||
      eventLocationType === locationTypes.BOTH
    ) {
      <section class="online-meeting-url-container">
        <p>{{ eventTitle.ONLINE_MEETING }}</p>
        <mat-form-field
          appearance="outline"
          subscriptSizing="dynamic"
          class="meeting-url"
        >
          <mat-label>{{ eventTitle.MEETING_URL }}</mat-label>
          <input
            matInput
            placeholder="Add meeting URL here"
            formControlName="meetingUrl"
          />
        </mat-form-field>
        @if (
          eventForm.get("meetingUrl")?.hasError("required") &&
          eventForm.get("meetingUrl")?.touched
        ) {
          <mat-error class="matError">
            {{ eventMessages.URL_REQUIRED }}
          </mat-error>
        }
        @if (
          eventForm.get("meetingUrl")?.hasError("url") &&
          eventForm.get("meetingUrl")?.touched
        ) {
          <mat-error class="matError">
            {{ eventMessages.VALID_URL }}
          </mat-error>
        }
      </section>
    }
  </section>

  @if (!isEdit) {
    <section class="rsvp-information">
      <h3>{{ eventMessages.RSVP_INFO }}</h3>
      <section class="rsvp-checkboxes">
        <mat-checkbox color="primary" formControlName="dietaryRequirements">{{
          eventMessages.DIETARY_REQUIREMENTS
        }}</mat-checkbox>
        <mat-checkbox color="primary" formControlName="additionalComments">{{
          eventMessages.ALLOW_NOTES
        }}</mat-checkbox>
      </section>
    </section>
  }
</form>
